// Handles EmptyState and ErrorState
import { useMemo, useState } from 'react';
import { DataTable } from '@repo/web-list';
import { defineListPage } from '@repo/list';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { useToast } from '../../../components/ui/Toast';
import { auditHooks, useExportAuditEvents } from '../hooks';
import { AuditLogFilterBar } from '../components/AuditLogFilterBar';
import { AuditEventDetailDrawer } from '../components/AuditEventDetailDrawer';
import { type AuditEventListItem, type AuditQueryFilters } from '../types';
import { PageHeader } from '../../../components/layout/PageHeader';
import { useDrawerParam } from '../../../lib/hooks';

const dateFormatter = new Intl.DateTimeFormat('en-IN', {
 day: '2-digit',
 month: 'short',
 year: 'numeric',
 hour: '2-digit',
 minute: '2-digit',
 second: '2-digit',
 hour12: false,
});

export function AuditLogPage() {
 const { toast } = useToast();
 const [filters, setFilters] = useState<AuditQueryFilters>({
 from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
 to: new Date().toISOString().split('T')[0],
 page: 1,
 limit: 50,
 });

  const { id: selectedEventId, open: openDrawer, close: closeDrawer } = useDrawerParam('drawer');
 
 const apiFilters = useMemo(() => {
 const f = { ...filters };
 if (f.from) f.from = new Date(`${f.from}T00:00:00.000Z`).toISOString();
 if (f.to) f.to = new Date(`${f.to}T23:59:59.999Z`).toISOString();
 return f;
 }, [filters]);

 const { data: listResponse, isLoading, error } = auditHooks.useList(apiFilters);
 const { data: detailResponse } = auditHooks.useGet(selectedEventId || '', {
 enabled: !!selectedEventId,
 });

 const exportMutation = useExportAuditEvents();

  const config = defineListPage<AuditEventListItem>({
  variant: 'data-table',
  resource: 'items',
  columns: [
  { id: 'occurredAt', header: 'Timestamp', accessor: 'occurredAt', renderType: 'datetime' },
  { id: 'actorId', header: 'Actor', accessor: 'actorId', renderType: 'text' },
  { id: 'eventType', header: 'Event Type', accessor: 'eventType', renderType: 'text' },
  { id: 'resource', header: 'Resource', accessor: 'resourceType', renderType: 'text' },
  { id: 'summary', header: 'Summary', accessor: 'summary', renderType: 'text' },
  { id: 'ipAddress', header: 'IP Address', accessor: 'ipAddress', renderType: 'text' },
  ],
  onRowClick: (row) => openDrawer(row.id),
  });

  const handleExport = () => {
  exportMutation.mutate(apiFilters, {
  onSuccess: () => {
  toast({
  type: 'success',
  title: 'Export queued',
  description: "You'll be notified when the CSV is ready for download.",
  });
  },
  onError: (err) => {
  toast({
  type: 'error',
  title: 'Export failed',
  description: err instanceof Error ? err.message : 'Unknown error',
  });
  },
  });
  };

  return (
   <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
   <PageHeader
   title="Audit Log"
   subtitle="Monitor and investigate system events and user actions."
   />
   <AuditLogFilterBar
   filters={filters}
   onFiltersChange={setFilters}
   onExport={handleExport}
   exportLoading={exportMutation.isPending}
   />
   <div className="flex-1 w-full min-h-0">
  <div className="flex-1 min-h-0 bg-surface-1 border border-border rounded-[12px] overflow-hidden flex flex-col h-full">
  <DataTable onRetry={() => window.location.reload()}
  config={config}
  data={listResponse?.data || []}
  total={listResponse?.meta?.total || 0}
  page={filters.page}
  limit={filters.limit}
  onPageChange={(page) => setFilters({ ...filters, page })}
  loading={isLoading}
  error={error instanceof Error ? error.message : undefined}
  className="flex-1 h-full"
  customRenderers={{
  occurredAt: (row: AuditEventListItem) => dateFormatter.format(new Date(row.occurredAt)),
  actorId: (row: AuditEventListItem) => {
  if (!row.actorId) return <span className="text-text-secondary">System</span>;
  return (
  <div className="flex flex-col" title={row.actorId}>
  <span className="text-text-primary font-mono text-xs truncate max-w-[120px]">
  {row.actorId}
  </span>
  <span className="text-caption text-text-tertiary uppercase">{row.actorAudience}</span>
  </div>
  );
  },
  eventType: (row: AuditEventListItem) => (
  <StatusBadge status="info" label={row.eventType} dot={false} className="font-mono text-caption" />
  ),
  resource: (row: AuditEventListItem) => {
  if (!row.resourceType) return '-';
  return (
  <div className="flex flex-col">
  <span className="text-text-primary font-medium text-xs">{row.resourceType}</span>
  <span className="text-caption text-text-tertiary font-mono truncate max-w-[100px]">
  {row.resourceId}
  </span>
  </div>
  );
  },
  summary: (row: AuditEventListItem) => (
  <span className="text-xs text-text-secondary line-clamp-1" title={row.summary}>
  {row.summary}
  </span>
  ),
  ipAddress: (row: AuditEventListItem) => row.ipAddress || '-',
  }}
  />
  </div>

  <AuditEventDetailDrawer
  event={detailResponse || null}
  open={!!selectedEventId}
  onClose={closeDrawer}
 />
  </div>
  </div>
  );
}
